import { PlusCircle, XCircle } from 'lucide-react';
import { forwardRef } from 'react';

import { TagTriggerTag } from '@/components/ui/inputs/combobox-multi/trigger/tag';
import { VisuallyHidden } from '@/components/visually-hidden';
import { OptionItem } from '@/types';
import { tv } from '@/utils/styles';

export interface ComboboxMultiTriggerProps {
  selectedValues: string[];
  onTogglePopover: () => void;
  onValueChange: (value: string[]) => void;
  maxCount?: number;
  options: OptionItem[];
  placeholder: string;
  className?: string;
  isOpen?: boolean;
}

const ComboboxMultiTrigger = forwardRef<HTMLButtonElement, ComboboxMultiTriggerProps>(
  ({ selectedValues, onTogglePopover, maxCount = 1, options, onValueChange, placeholder, className, isOpen }, ref) => {
    const finalMaxCount = selectedValues.length > maxCount + 1 ? maxCount : selectedValues.length;
    const visibleOptions = selectedValues.slice(0, finalMaxCount);
    const hiddenOptions = selectedValues.slice(finalMaxCount, selectedValues.length);

    const { base, selectedValuesWrapper, selectedValuesInner, content, icon } = sharedComboBoxStyles({
      hasItemsSelected: selectedValues.length !== 0,
    });

    return (
      <div className="relative">
        <button
          type="button"
          ref={ref}
          onClick={onTogglePopover}
          className={base({ className })}
          aria-expanded={isOpen || selectedValues.length !== 0}
        >
          <div className={content()}>
            <span>{placeholder}</span>
            {visibleOptions.length > 0 ? (
              <div className={selectedValuesWrapper()}>
                <ul className={selectedValuesInner()}>
                  {visibleOptions.map((value) => {
                    const option = options.find((o) => o.value === value);
                    return (
                      <li key={value}>
                        <TagTriggerTag title={option?.label} label={option?.label} icon={option?.icon} />
                      </li>
                    );
                  })}
                  {hiddenOptions.length > 0 && (
                    <li>
                      <TagTriggerTag
                        title={hiddenOptions
                          .map((value) => {
                            const option = options.find((o) => o.value === value);
                            return option?.label;
                          })
                          .join(', ')}
                        label={`+ ${hiddenOptions.length} more`}
                      />
                    </li>
                  )}
                </ul>
              </div>
            ) : null}
          </div>
        </button>
        {selectedValues.length > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onValueChange([]);
            }}
            title="Clear filter"
            aria-label="Clear filter"
            className={icon({ className: 'hover:text-errorDark' })}
          >
            <XCircle className="size-4 min-w-4" />
            <VisuallyHidden>Clear filter</VisuallyHidden>
          </button>
        ) : (
          <PlusCircle className={icon({ className: 'pointer-events-none' })} />
        )}
      </div>
    );
  },
);

ComboboxMultiTrigger.displayName = 'ComboboxMultiTrigger';

export const sharedComboBoxStyles = tv({
  slots: {
    base: 'min-h-[--input-height-small] rounded-md border border-dashed border-grey-350 bg-grey-100 pl-7 !text-baseSm font-normal text-text-secondary hover:border-grey-600 hover:text-text-primary aria-expanded:border-grey-600 aria-expanded:text-text-primary',
    selectedValuesWrapper: 'flex w-full items-center justify-between',
    selectedValuesInner: 'flex items-center gap-1.5',
    content: 'flex items-center gap-1.5 pr-1.5',
    tagWrapper: 'flex items-center overflow-hidden rounded-default',
    tagLabel: 'bg-grey-100',
    tagButton: 'flex bg-lightBlue-500 text-white',
    icon: 'absolute left-2 top-1/2 size-4 -translate-y-1/2',
  },
  variants: {
    hasItemsSelected: {
      true: {
        base: 'border-solid bg-white text-text-primary',
      },
    },
  },
});

export { ComboboxMultiTrigger };
